'use client';

/**
 * Checkbox — 16 × 16 px square (theme T5a §5).  Supports indeterminate state.
 *
 * Accessibility:
 *   - Full label + box is a single click target — wrap in <FormField>
 *     or a `<label>` yourself.
 *   - Focus ring renders on the visible wrapper via `has-[input:focus-visible]`
 *     (F6 audit F6-01); the inner `<input>` is opacity-0 so the native
 *     ring isn't visible.
 */
import { Check, Minus } from 'lucide-react';
import type { InputHTMLAttributes } from 'react';
import { forwardRef, useCallback, useEffect, useRef } from 'react';

import { cn } from './cn';

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  indeterminate?: boolean;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { indeterminate = false, className, checked, disabled, ...rest },
  forwardedRef,
) {
  const internalRef = useRef<HTMLInputElement | null>(null);

  // Single callback ref merges internal + forwarded refs synchronously
  // during React's commit phase — consumers can focus the node from
  // their own effects without waiting for an extra render (F6-02).
  const setRefs = useCallback(
    (node: HTMLInputElement | null): void => {
      internalRef.current = node;
      if (typeof forwardedRef === 'function') forwardedRef(node);
      else if (forwardedRef) forwardedRef.current = node;
    },
    [forwardedRef],
  );

  useEffect(() => {
    if (internalRef.current) internalRef.current.indeterminate = indeterminate;
  }, [indeterminate]);

  return (
    <span
      className={cn(
        'relative inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border bg-white transition-colors duration-fast ease-standard',
        'has-[input:focus-visible]:ring-2 has-[input:focus-visible]:ring-brand-primary-400 has-[input:focus-visible]:ring-offset-2',
        checked || indeterminate
          ? 'border-brand-primary-600 bg-brand-primary-600'
          : 'border-slate-400 hover:border-slate-600',
        disabled && 'cursor-not-allowed opacity-disabled',
        className,
      )}
    >
      <input
        ref={setRefs}
        checked={checked}
        className="absolute inset-0 h-full w-full cursor-pointer appearance-none rounded-sm opacity-0 focus:outline-none"
        disabled={disabled}
        type="checkbox"
        {...rest}
      />
      {indeterminate ? (
        <Minus aria-hidden className="text-white" size={10} strokeWidth={3} />
      ) : checked ? (
        <Check aria-hidden className="text-white" size={10} strokeWidth={3} />
      ) : null}
    </span>
  );
});
